import React, { useEffect, useState, useCallback, useRef } from 'react';
import { StatusBadge } from '../shared/StatusBadge';
import LoadingSpinner from '../shared/LoadingSpinner';
import { formatDateTime } from '../../utils/dateFormatters';
import {
    TabContent,
    DetailPanel,
    TabContentWrapper,
    ListDetailLayout,
    ListPanel,
    ListFilterBar,
    FileUploadContainer,
    FileUploadRow,
    HiddenFileInput,
    UploadButton,
    FileLink,
    SuccessMessage,
    ErrorMessage,
    EditActionsBar,
} from '../../pages/admin/AdminPage.styles';
import { EditableInput } from '../product/EditableFields.styles';
import { useEditMode } from '../../context/EditModeContext';
import { useAuthStore } from '../../context/AuthContext';
import useUpdateDraegerTube from '../../api/useUpdateDraegerTube';
import useGetProductsByDraegerTube from '../../api/useGetProductsByDraegerTube';
import { useUploadFile } from '../../api/useUploadFile';
import { useDownloadFile } from '../../api/useDownloadFile';
import ProductsList from '../shared/ProductsList';

interface DraegerTubeTabProps {
    draegerTubes: any[];
    draegerLoading: boolean;
    draegerError: string | null;
    selectedDraegerTube: any;
    setSelectedDraegerTube: (tube: any) => void;
    onRefresh?: () => void;
}

const DraegerTubeTab: React.FC<DraegerTubeTabProps> = ({
    draegerTubes,
    draegerLoading,
    draegerError,
    selectedDraegerTube,
    setSelectedDraegerTube,
    onRefresh,
}) => {
    const { products, loading: productsLoading, error: productsError, getProductsByDraegerTube } = useGetProductsByDraegerTube();
    const { updateDraegerTube, isLoading: isUpdating } = useUpdateDraegerTube();
    const { uploadFile, isUploading, uploadError, uploadSuccess, clearMessages } = useUploadFile();
    const { downloadFile, downloadError } = useDownloadFile();
    const { isInEditMode, setEditMode } = useEditMode();
    const { loggedInUser } = useAuthStore();
    const [selectedDraegerTubeId, setSelectedDraegerTubeId] = useState<number | null>(null);
    const [formData, setFormData] = useState<any>(null);
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('active');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const canEdit = loggedInUser !== null;

    // Initialize form data when draeger tube is selected
    useEffect(() => {
        if (selectedDraegerTube) {
            setFormData(selectedDraegerTube);
            // Don't reset edit mode if this is a new item (id === 0)
            if (selectedDraegerTube.id !== 0) {
                setEditMode(false);
            }
        }
    }, [selectedDraegerTube, setEditMode]);

    // Handle edit mode
    const handleEdit = useCallback(() => {
        setEditMode(true);
        setUpdateError(null);
    }, [setEditMode]);

    // Handle add new draeger tube
    const handleAddNew = useCallback(() => {
        const newDraegerTube = {
            id: 0,
            orderCode: '',
            description: '',
            isNoDraegerTubeAvailable: false,
            filePath: '',
            lastUpdatedUtc: new Date().toISOString(),
        };
        setSelectedDraegerTube(newDraegerTube);
        setEditMode(true);
        setUpdateError(null);
    }, [setEditMode]);

    // Handle cancel
    const handleCancel = useCallback(() => {
        setFormData(selectedDraegerTube);
        setEditMode(false);
        setUpdateError(null);
    }, [selectedDraegerTube, setEditMode]);

    // Handle save
    const handleSave = useCallback(async () => {
        if (!formData) return;

        try {
            await updateDraegerTube(formData);
            setEditMode(false);
            setUpdateError(null);
            // Refresh the list to show updated data
            onRefresh?.();
        } catch (error: any) {
            setUpdateError(error.message);
        }
    }, [formData, updateDraegerTube, setEditMode, onRefresh]);

    // Handle field changes
    const handleFieldChange = useCallback((field: string, value: any) => {
        setFormData((prev: any) => {
            if (!prev) return prev;
            return {
                ...prev,
                [field]: value,
            };
        });
    }, []);

    // Handle deactivate/activate toggle
    const handleToggleActive = useCallback(async () => {
        if (!selectedDraegerTube || selectedDraegerTube.id === 0) return;
        const action = selectedDraegerTube.isNoDraegerTubeAvailable ? 'activate' : 'deactivate';
        if (!window.confirm(`Are you sure you want to ${action} "${selectedDraegerTube.description || selectedDraegerTube.orderCode}"?`)) return;

        try {
            await updateDraegerTube({ ...selectedDraegerTube, isNoDraegerTubeAvailable: !selectedDraegerTube.isNoDraegerTubeAvailable });
            setUpdateError(null);
            onRefresh?.();
        } catch (error: any) {
            setUpdateError(error.message);
        }
    }, [selectedDraegerTube, updateDraegerTube, onRefresh]);

    // Handle file upload
    const handleFileUpload = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (!file || !formData) return;

            try {
                clearMessages();
                const fileName = file.name;
                await uploadFile({
                    category: 'draegertube',
                    fileName,
                    file,
                });

                // Update form data with new file path
                handleFieldChange('filePath', fileName);
            } catch (error) {
                console.error('File upload failed:', error);
            }

            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        },
        [formData, uploadFile, handleFieldChange, clearMessages]
    );

    // Fetch products when a draeger tube is selected
    useEffect(() => {
        if (selectedDraegerTube?.id && selectedDraegerTube.id !== selectedDraegerTubeId) {
            setSelectedDraegerTubeId(selectedDraegerTube.id);
            getProductsByDraegerTube(selectedDraegerTube.id);
        }
    }, [selectedDraegerTube, selectedDraegerTubeId, getProductsByDraegerTube]);

    // Filter draeger tubes by active status
    const filteredDraegerTubes = draegerTubes.filter((tube) => {
        if (activeFilter === 'active') return !tube.isNoDraegerTubeAvailable;
        if (activeFilter === 'inactive') return tube.isNoDraegerTubeAvailable;
        return true;
    });

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!filteredDraegerTubes.length) return;

            const currentIndex = filteredDraegerTubes.findIndex((t) => t.id === selectedDraegerTube?.id);

            switch (event.key) {
                case 'ArrowDown': {
                    event.preventDefault();
                    const nextIndex = currentIndex < filteredDraegerTubes.length - 1 ? currentIndex + 1 : 0;
                    setSelectedDraegerTube(filteredDraegerTubes[nextIndex]);
                    break;
                }
                case 'ArrowUp': {
                    event.preventDefault();
                    const prevIndex = currentIndex > 0 ? currentIndex - 1 : filteredDraegerTubes.length - 1;
                    setSelectedDraegerTube(filteredDraegerTubes[prevIndex]);
                    break;
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [filteredDraegerTubes, selectedDraegerTube, setSelectedDraegerTube]);
    return (
        <TabContent>
            <ListDetailLayout>
                <ListPanel>
                    <ListFilterBar>
                        <span className="filter-label">Show:</span>
                        <button className={`filter-btn ${activeFilter === 'all' ? 'active' : ''}`} onClick={() => setActiveFilter('all')}>All</button>
                        <button className={`filter-btn ${activeFilter === 'active' ? 'active' : ''}`} onClick={() => setActiveFilter('active')}>Active</button>
                        <button className={`filter-btn ${activeFilter === 'inactive' ? 'active' : ''}`} onClick={() => setActiveFilter('inactive')}>Inactive</button>
                        <span className="filter-count">{filteredDraegerTubes.length} of {draegerTubes.length}</span>
                    </ListFilterBar>

                    {draegerLoading && <LoadingSpinner text="Loading draeger tubes..." />}

                    {draegerError && (
                        <div className="error-state">
                            <i className="fas fa-exclamation-triangle"></i> Error loading draeger tubes: {draegerError}
                        </div>
                    )}

                    {!draegerLoading && !draegerError && draegerTubes.length === 0 && (
                        <div className="empty-state">
                            <i className="fas fa-vial"></i>
                            <p>No draeger tubes found</p>
                        </div>
                    )}

                    {!draegerLoading && draegerTubes.length > 0 && (
                        <div className="table-container">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Active</th>
                                        <th>Order Code</th>
                                        <th>Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredDraegerTubes.map((tube) => (
                                        <tr
                                            key={tube.id}
                                            className={`table-row ${tube.isNoDraegerTubeAvailable ? 'inactive' : ''} ${
                                                selectedDraegerTube?.id === tube.id ? 'selected' : ''
                                            }`}
                                            onClick={() => setSelectedDraegerTube(tube)}
                                        >
                                            <td>
                                                <StatusBadge variant={tube.isNoDraegerTubeAvailable ? 'inactive' : 'active'} size="list">
                                                    {tube.isNoDraegerTubeAvailable ? 'Inactive' : 'Active'}
                                                </StatusBadge>
                                            </td>
                                            <td className="order-code-cell">{tube.orderCode}</td>
                                            <td className="description-cell">{tube.description}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </ListPanel>

                <TabContentWrapper>
                    <DetailPanel>
                        {selectedDraegerTube && formData ? (
                            <>
                                {isInEditMode && (
                                    <EditActionsBar>
                                        <button onClick={handleAddNew} className="btn-add-new" title="Add new draeger tube">
                                            <i className="fas fa-plus"></i> New Dräger Tube
                                        </button>
                                        <div>
                                            {selectedDraegerTube?.id !== 0 && (
                                                <button
                                                    onClick={handleToggleActive}
                                                    className={selectedDraegerTube?.isNoDraegerTubeAvailable ? 'btn-activate' : 'btn-deactivate'}
                                                    disabled={isUpdating}
                                                    title={selectedDraegerTube?.isNoDraegerTubeAvailable ? 'Activate this Dräger tube' : 'Deactivate this Dräger tube'}
                                                >
                                                    <i className={selectedDraegerTube?.isNoDraegerTubeAvailable ? 'fas fa-check' : 'fas fa-trash-alt'}></i>
                                                    {selectedDraegerTube?.isNoDraegerTubeAvailable ? 'Activate' : 'Deactivate'}
                                                </button>
                                            )}
                                            <button onClick={handleSave} className="btn-save" disabled={isUpdating} title="Save changes">
                                                <i className="fas fa-save"></i> {isUpdating ? 'Saving...' : 'Save'}
                                            </button>
                                            <button onClick={handleCancel} className="btn-cancel" disabled={isUpdating} title="Cancel changes">
                                                <i className="fas fa-times"></i> Cancel
                                            </button>
                                        </div>
                                    </EditActionsBar>
                                )}
                                <div className="detail-content">
                                    <div className="detail-header">
                                        <h3>{formData.description}</h3>

                                        <div>
                                            <div className="header-actions">
                                                <StatusBadge variant={formData.isNoDraegerTubeAvailable ? 'inactive' : 'active'} size="medium">
                                                    {formData.isNoDraegerTubeAvailable ? 'Inactive' : 'Active'}
                                                </StatusBadge>
                                                {canEdit && !isInEditMode && selectedDraegerTube?.id !== 0 && (
                                                    <button
                                                        onClick={handleToggleActive}
                                                        className={`edit-btn ${formData.isNoDraegerTubeAvailable ? 'activate' : 'deactivate'}`}
                                                        title={formData.isNoDraegerTubeAvailable ? 'Activate this Dräger tube' : 'Deactivate this Dräger tube'}
                                                        style={{
                                                            color: formData.isNoDraegerTubeAvailable ? '#28a745' : '#dc3545',
                                                        }}
                                                    >
                                                        <i className={formData.isNoDraegerTubeAvailable ? 'fas fa-check' : 'fas fa-trash-alt'}></i>
                                                    </button>
                                                )}
                                                {canEdit && !isInEditMode && (
                                                    <button onClick={handleEdit} className="edit-btn" title="Edit">
                                                        <i className="fas fa-pencil-alt"></i>
                                                    </button>
                                                )}
                                            </div>
                                            {formData.lastUpdatedUtc && (
                                                <div className="last-updated">Last updated: {formatDateTime(formData.lastUpdatedUtc)}</div>
                                            )}
                                        </div>
                                    </div>
                                    {updateError && (
                                        <div className="error-alert">
                                            <i className="fas fa-exclamation-triangle"></i> {updateError}
                                        </div>
                                    )}
                                    <div className="detail-body">
                                        <div className="detail-section">
                                            <h4>Basic Information</h4>
                                            <div className="detail-grid">
                                                <div className="detail-item">
                                                    <label>Order Code</label>
                                                    {isInEditMode ? (
                                                        <EditableInput
                                                            value={formData.orderCode || ''}
                                                            onChange={(e) => handleFieldChange('orderCode', e.target.value)}
                                                            placeholder="Enter order code"
                                                        />
                                                    ) : (
                                                        <span>{formData.orderCode}</span>
                                                    )}
                                                </div>
                                                <div className="detail-item">
                                                    <label>Description</label>
                                                    {isInEditMode ? (
                                                        <EditableInput
                                                            value={formData.description || ''}
                                                            onChange={(e) => handleFieldChange('description', e.target.value)}
                                                            placeholder="Enter description"
                                                        />
                                                    ) : (
                                                        <span>{formData.description}</span>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="detail-grid">
                                                <div className="detail-item">
                                                    <label>Dräger Tube Instructions</label>
                                                    <FileUploadContainer>
                                                        {isInEditMode ? (
                                                            <>
                                                                <FileUploadRow>
                                                                    <EditableInput
                                                                        value={formData.filePath || ''}
                                                                        onChange={(e) => handleFieldChange('filePath', e.target.value)}
                                                                        placeholder="Enter file name"
                                                                    />
                                                                    <UploadButton
                                                                        type="button"
                                                                        onClick={() => fileInputRef.current?.click()}
                                                                        disabled={isUploading}
                                                                        title="Upload new file"
                                                                    >
                                                                        <i className="fas fa-upload"></i>{' '}
                                                                        {isUploading ? 'Uploading...' : 'Upload New File'}
                                                                    </UploadButton>
                                                                    <HiddenFileInput
                                                                        ref={fileInputRef}
                                                                        type="file"
                                                                        accept=".pdf"
                                                                        onChange={handleFileUpload}
                                                                    />
                                                                </FileUploadRow>
                                                                {uploadSuccess && (
                                                                    <SuccessMessage>
                                                                        <i className="fas fa-check-circle"></i> File uploaded successfully
                                                                    </SuccessMessage>
                                                                )}
                                                                {uploadError && (
                                                                    <ErrorMessage>
                                                                        <i className="fas fa-exclamation-triangle"></i> {uploadError}
                                                                    </ErrorMessage>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <span>
                                                                {formData.filePath ? (
                                                                    <>
                                                                        <FileLink
                                                                            href="#"
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                downloadFile({
                                                                                    category: 'draegertube',
                                                                                    fileName: formData.filePath,
                                                                                });
                                                                            }}
                                                                        >
                                                                            <i className="fas fa-file-pdf"></i> {formData.filePath}
                                                                        </FileLink>
                                                                        {downloadError && (
                                                                            <ErrorMessage>
                                                                                <i className="fas fa-exclamation-triangle"></i> {downloadError}
                                                                            </ErrorMessage>
                                                                        )}
                                                                    </>
                                                                ) : (
                                                                    <span className="text-muted">No file</span>
                                                                )}
                                                            </span>
                                                        )}
                                                    </FileUploadContainer>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="detail-placeholder">
                                <i className="fas fa-vial"></i>
                                <h3>Select a draeger tube</h3>
                                <p>Choose a draeger tube from the list to view its details</p>
                            </div>
                        )}
                    </DetailPanel>

                    {/* Products using this Draeger tube - separate section outside DetailPanel */}
                    {selectedDraegerTube && (
                        <ProductsList products={products} loading={productsLoading} error={productsError} title="Products using this Dräger tube" />
                    )}
                </TabContentWrapper>
            </ListDetailLayout>
        </TabContent>
    );
};

export default DraegerTubeTab;
